<script setup lang="ts">
import {ShiftSuggestion} from "@/core/http/resources/TeamPlanningResource";
import {CalendarCheck, CircleAlert} from "@lucide/vue";

/**
 * One answer of the agent in the schedule thread: its questions with their
 * quick replies, or the proposal it just laid on the grid.
 */
const {answer, interactive} = defineProps<{
    answer: ShiftSuggestion;
    /** Quick replies only make sense on the answer awaiting a reply. */
    interactive: boolean;
}>();

const emits = defineEmits<{
    (event: 'reply', text: string): void;
}>();
</script>

<template>
  <div class="flex max-w-[90%] flex-col gap-2 self-start rounded-lg border border-solid border-gray-200 bg-white px-3 py-2 text-xs leading-snug text-gray-700">
    <p class="whitespace-pre-line">
      {{ answer.reply }}
    </p>

    <div v-for="question in answer.questions" :key="question.text" class="flex flex-col gap-1.5">
      <p class="font-semibold text-gray-800">
        {{ question.text }}
      </p>
      <div v-if="question.options.length" class="flex flex-wrap gap-1.5">
        <button
          v-for="option in question.options"
          :key="option"
          class="h-6 rounded-full border border-solid border-primary/30 bg-white px-2.5 text-[11px] font-semibold text-primary transition-colors enabled:hover:border-primary disabled:opacity-50"
          :disabled="!interactive"
          @click="emits('reply', option)"
        >
          {{ option }}
        </button>
      </div>
    </div>

    <div
      v-for="warning in answer.warnings"
      :key="warning"
      class="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800"
    >
      <CircleAlert class="mt-px size-3.5 shrink-0" />
      {{ warning }}
    </div>

    <p v-if="answer.proposal" class="flex items-center gap-1.5 text-[11px] font-semibold text-green-700">
      <CalendarCheck class="size-3.5 shrink-0" />
      Proposition appliquée sur la grille, pensez à enregistrer.
    </p>
  </div>
</template>
